import { alpha, createTheme, type Theme } from '@mui/material/styles';
import type { AppTheme } from '../main/theme/AppThemeContext';

// Token parity with the PoseidonFrontend design system (src/main/theme/theme.css): each app theme
// maps to the same surfaces, text scale and primary color, so MUI pages (customer hub, workspace
// dialogs) follow the theme the user picked instead of staying light blue.
interface ThemeTokens {
  mode: 'light' | 'dark';
  primary: string;
  primaryContrast: string;
  secondary: string;
  bg: string;
  paper: string;
  head: string;
  text: string;
  text2: string;
}

const TOKENS: Record<AppTheme, ThemeTokens> = {
  rose: { mode: 'light', primary: '#b03a62', primaryContrast: '#ffffff', secondary: '#d97a9c', bg: '#faf6f8', paper: '#ffffff', head: '#f8f1f4', text: '#2b2226', text2: '#4f3d46' },
  light: { mode: 'light', primary: '#2563eb', primaryContrast: '#ffffff', secondary: '#0ea5e9', bg: '#f6f8fc', paper: '#ffffff', head: '#f1f5f9', text: '#0f172a', text2: '#334155' },
  sky: { mode: 'light', primary: '#4f9cf9', primaryContrast: '#ffffff', secondary: '#7cc6ff', bg: '#eaf5ff', paper: '#f8fbff', head: '#e3f0ff', text: '#12324d', text2: '#33526d' },
  pearl: { mode: 'light', primary: '#7c3aed', primaryContrast: '#ffffff', secondary: '#06b6d4', bg: '#fbfbff', paper: '#ffffff', head: '#f6f7fb', text: '#111827', text2: '#374151' },
  'brown-white': { mode: 'light', primary: '#8b5e3c', primaryContrast: '#ffffff', secondary: '#b7794b', bg: '#f8f3ed', paper: '#fffdf9', head: '#f2e8dc', text: '#2f2119', text2: '#584437' },
  // Dark variants use the calm overrides at the end of theme.css.
  brown: { mode: 'dark', primary: '#b89a82', primaryContrast: '#241c17', secondary: '#c1aa96', bg: '#29231f', paper: '#332c27', head: '#3d3530', text: '#f2ede8', text2: '#d8cec5' },
  dark: { mode: 'dark', primary: '#8da9c4', primaryContrast: '#17202a', secondary: '#91b5bc', bg: '#20242b', paper: '#292e36', head: '#313741', text: '#eef0f3', text2: '#ccd1d8' },
  midnight: { mode: 'dark', primary: '#aaa3c7', primaryContrast: '#211f2e', secondary: '#b9a6bc', bg: '#252535', paper: '#2e2e40', head: '#37374a', text: '#f0eff4', text2: '#d2cfda' },
};

export function createPlatformTheme(appTheme: AppTheme = 'light'): Theme {
  const tokens = TOKENS[appTheme] ?? TOKENS.light;
  const line = alpha(tokens.text, tokens.mode === 'dark' ? 0.16 : 0.12);
  const softLine = alpha(tokens.text, tokens.mode === 'dark' ? 0.1 : 0.08);
  return createTheme({
    palette: {
      mode: tokens.mode,
      primary: { main: tokens.primary, contrastText: tokens.primaryContrast },
      secondary: { main: tokens.secondary },
      success: { main: '#16a34a' },
      warning: { main: '#f59e0b' },
      error: { main: '#ef4444' },
      background: { default: tokens.bg, paper: tokens.paper },
      text: { primary: tokens.text, secondary: tokens.text2 },
      divider: softLine,
    },
    shape: {
      borderRadius: 12,
    },
    typography: {
      fontFamily: [
        '"Segoe UI"',
        'system-ui',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        'sans-serif',
      ].join(','),
      h1: { fontWeight: 800 },
      h2: { fontWeight: 800 },
      h3: { fontWeight: 800 },
      h4: { fontWeight: 800 },
      h5: { fontWeight: 800 },
      h6: { fontWeight: 700 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: { minHeight: 42, borderRadius: 11, paddingInline: 18, transition: 'background-color 160ms ease, box-shadow 160ms ease, border-color 160ms ease', '&.Mui-focusVisible': { outline: `2px solid ${tokens.primary}`, outlineOffset: 2 } },
          sizeSmall: { minHeight: 34, paddingInline: 12, borderRadius: 9, '@media (pointer: coarse)': { minHeight: 40 } },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: { borderRadius: 11, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderWidth: 2 } },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 18,
            border: `1px solid ${line}`,
            backgroundImage: 'none',
            boxShadow: tokens.mode === 'dark' ? '0 8px 28px rgba(0,0,0,.3)' : '0 8px 28px rgba(15,23,42,.06)',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: { backgroundImage: 'none' },
          outlined: { borderColor: line },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: { borderRadius: 24, border: `1px solid ${line}`, backgroundImage: 'none', boxShadow: '0 30px 90px rgba(10,15,25,.28)', '@media (max-width:600px)': { margin: 12, width: 'calc(100% - 24px)', maxHeight: 'calc(100% - 24px)', borderRadius: 18 } },
        },
      },
      MuiDialogTitle: {
        styleOverrides: { root: { padding: '24px 28px', fontSize: 22, fontWeight: 750, letterSpacing: '-.02em', borderBottom: `1px solid ${softLine}`, '@media (max-width:600px)': { padding: '18px 20px', fontSize: 19 } } },
      },
      MuiDialogContent: {
        styleOverrides: { root: { padding: '24px 28px', '&.MuiDialogContent-root': { paddingTop: 24 }, '@media (max-width:600px)': { padding: '18px 20px', '&.MuiDialogContent-root': { paddingTop: 18 } } } },
      },
      MuiDialogActions: {
        styleOverrides: { root: { padding: '18px 28px', gap: 10, borderTop: `1px solid ${softLine}`, '@media (max-width:600px)': { padding: '14px 20px', '& > .MuiButton-root': { flex: 1 } } } },
      },
      MuiBackdrop: { styleOverrides: { root: { backgroundColor: 'rgba(17,24,39,.48)', backdropFilter: 'blur(8px)' } } },
      MuiTableCell: {
        styleOverrides: { head: { fontSize: 12, fontWeight: 700, padding: '12px 18px', backgroundColor: tokens.head, color: tokens.text2, whiteSpace: 'nowrap' }, body: { padding: '14px 18px', fontSize: 13.5, fontVariantNumeric: 'tabular-nums' } },
      },
      MuiChip: { styleOverrides: { root: { borderRadius: 999, fontWeight: 600 } } },
      MuiAlert: { styleOverrides: { root: { borderRadius: 12 } } },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: 8,
            marginBottom: 4,
            '&.Mui-selected': { backgroundColor: alpha(tokens.primary, 0.12), color: tokens.primary },
            '&.Mui-selected .MuiListItemIcon-root': { color: tokens.primary },
          },
        },
      },
      MuiListItemIcon: { styleOverrides: { root: { minWidth: 40 } } },
    },
  });
}

export const platformTheme = createPlatformTheme('light');
